import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { Subject, takeUntil, takeWhile, timer } from 'rxjs';
import { ModalComponent } from '../modal/modal.component';

const AUTO_LOGOUT_TIME_MS = 30 * 1000;

@Component({
  standalone: true,
  selector: 'app-session-dialog',
  templateUrl: './session-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalComponent],
})
export class SessionDialogComponent implements OnInit, OnDestroy {
  readonly header = input('Inactive Session');
  readonly body = input('Your session is about to expire due to inactivity.');
  readonly positiveButtonText = input('Continue session');
  readonly negativeButtonText = input('Log out');
  readonly dismissOnActionSelect = input(true);
  readonly showLoadingOnActionClick = input(true);
  readonly autoLogOutTimeMs = input(AUTO_LOGOUT_TIME_MS);

  readonly onExitClicked = output<void>();
  readonly onRefreshClicked = output<void>();

  readonly loading = input(false);
  readonly timeoutMs = signal(AUTO_LOGOUT_TIME_MS);

  private readonly _destroy$ = new Subject<void>();

  readonly seconds = computed(() => Math.floor(this.timeoutMs() / 1000));
  readonly minutes = computed(() => Math.floor(this.seconds() / 60));
  readonly displaySeconds = computed(() =>
    Math.max(0, this.seconds() % 60)
      .toString()
      .padStart(2, '0'),
  );
  readonly displayMinutes = computed(() =>
    Math.max(0, this.minutes() % 60)
      .toString()
      .padStart(2, '0'),
  );
  readonly isCritical = computed(() => this.timeoutMs() <= 10 * 1000);

  constructor() {}

  ngOnInit(): void {
    this.timeoutMs.set(this.autoLogOutTimeMs());
    this.initCounter();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  initCounter(interval: number = 1000) {
    timer(0, interval)
      .pipe(
        takeWhile(() => this.timeoutMs() > 0, true),
        // Stop if component destroyed or user manually closes modal
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (value) => {
          this.timeoutMs.update((timeoutMs) => timeoutMs - interval);
          if (this.timeoutMs() <= 0) this.exit();
        },
      });
  }

  close() {
    this._destroy$.next();
  }

  exit() {
    if (this.dismissOnActionSelect()) {
      this.close();
    }
    this.onExitClicked.emit();
  }

  refresh() {
    this.onRefreshClicked.emit();
  }
}
